<template>
  <section class="admin-content" id="contact-search">
    <Navbar/>
    <main class="admin-main">

      <!-- ── Page Header ─────────────────────────────────────── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i class="fe fe-shield" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">HMOs / TPAs</h4>
              <small style="opacity: .8;">{{ tpas.length }} registered TPAs</small>
            </div>
            <div class="ml-auto">
              <button type="button" class="btn btn-light btn-sm font-weight-bold px-4" @click="show = !show">
                <i class="fe mr-1" :class="show ? 'fe-minus' : 'fe-plus'"></i>
                {{ show ? 'Cancel' : 'Add TPA' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- ── Add TPA Form ────────────────────────────────────── -->
        <div v-show="show" class="card form-card shadow mb-4">
          <div class="form-card-header">
            <div class="section-number">1</div>
            <h6 class="mb-0 font-weight-bold text-white">Register New TPA</h6>
          </div>
          <div class="card-body px-4 pt-4 pb-3">
            <div class="form-row">
              <div class="form-group col-md-12">
                <label class="small font-weight-bold text-secondary">Category</label>
                <select class="form-control" v-model="category">
                  <option value="A">Category A</option>
                  <option value="B">Category B</option>
                </select>
              </div>

              <div class="form-group col-md-4">
                <label for="tpa-code" class="small font-weight-bold text-secondary">HMO Code</label>
                <input id="tpa-code" type="text" maxlength="191" class="form-control" placeholder="e.g. HYG-01" v-model.trim="register.tpa_id">
                <small class="form-text text-muted">Printed on enrollees' ID cards.</small>
              </div>
              <div class="form-group col-md-4">
                <label class="small font-weight-bold text-secondary">First Name</label>
                <input type="text" required class="form-control" placeholder="First Name" v-model="register.firstname">
              </div>
              <div class="form-group col-md-4">
                <label class="small font-weight-bold text-secondary">Last Name</label>
                <input type="text" required class="form-control" placeholder="Last Name" v-model="register.lastname">
              </div>

              <div class="form-group col-md-6">
                <label class="small font-weight-bold text-secondary">Organization Name</label>
                <input type="text" required class="form-control" placeholder="Organization Name" v-model="register.organization_name">
              </div>

              <div class="form-group col-md-6">
                <label class="small font-weight-bold text-secondary">Phone Number</label>
                <input v-phone type="text" required class="form-control" placeholder="Phone Number" v-model="register.phone_number">
              </div>
              <div class="form-group col-md-4">
                <label class="small font-weight-bold text-secondary">Email</label>
                <input type="email" required class="form-control" placeholder="Email" v-model="register.email">
              </div>
              <div class="form-group col-md-4">
                <label class="small font-weight-bold text-secondary">Password</label>
                <input type="password" required class="form-control" placeholder="Password" v-model="register.password">
              </div>

              <div class="form-group col-md-4">
                <label class="small font-weight-bold text-secondary">Confirm Password</label>
                <input type="password" required class="form-control" placeholder="Confirm Password" v-model="register.password_confirmation">
              </div>
              <div class="form-group col-md-12">
                <label for="tpa-address" class="small font-weight-bold text-secondary">Office Address</label>
                <input id="tpa-address" type="text" maxlength="1000" class="form-control"
                  placeholder="e.g. 12 Aba Road, Port Harcourt" v-model.trim="register.address">
              </div>
            </div>

            <div class="d-flex justify-content-end">
              <button @click="registerUser" class="btn btn-submit px-5">
                <i class="fe fe-send mr-2"></i> Add TPA
              </button>
            </div>
          </div>
        </div>

        <!-- ── TPA Table ───────────────────────────────────────── -->
        <div class="card table-card shadow">
          <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
            <div>
              <h6 class="mb-0 font-weight-bold text-dark">
                <i class="fe fe-activity text-primary mr-2"></i> Registered TPAs
              </h6>
              <small class="text-muted">{{ tpas.length }} record(s)</small>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th px-4">#</th>
                  <th class="table-th">Category</th>
                  <th class="table-th">Name of TPA</th>
                  <th class="table-th">HMO Code</th>
                  <th class="table-th">Email</th>
                  <th class="table-th">Phone Number</th>
                  <th class="table-th">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(tpa, index) in tpas" v-bind:key="tpa.id" class="table-row">
                  <td class="px-4 py-3">{{ index + 1 }}</td>
                  <td class="py-3"><span class="code-chip">{{ tpa.category }}</span></td>
                  <td class="py-3" style="font-size: .88rem;">
                    <div class="d-flex align-items-center" style="gap: 10px;">
                      <div class="employee-avatar">
                        {{ (tpa.organization_name || '?')[0].toUpperCase() }}
                      </div>
                      <p class="mb-0 font-weight-bold" style="font-size: .88rem;">
                        {{ tpa.organization_name }}
                      </p>
                    </div>
                  </td>
                  <td class="py-3" style="font-size: .88rem;">
                    <span v-if="tpa.tpa_id" class="rel-chip">{{ tpa.tpa_id }}</span><span v-else class="text-muted">—</span>
                  </td>
                  <td class="py-3" style="font-size: .88rem;">{{ tpa.email }}</td>
                  <td class="py-3" style="font-size: .88rem;">{{ tpa.phone_number }}</td>
                  <td class="py-3">
                    <div class="d-flex" style="gap: 6px;">
                      <router-link :to="'/tpa/' + tpa.id" class="btn btn-sm btn-outline-primary">
                        <i class="fe fe-eye mr-1"></i> View
                      </router-link>
                      <router-link v-if="canManage" :to="'/edit-tpa/' + tpa.id" class="btn btn-sm btn-outline-secondary">
                        <i class="fe fe-edit-2 mr-1"></i> Edit
                      </router-link>
                    </div>
                  </td>
                </tr>
                <tr v-if="!tpas || tpas.length === 0">
                  <td colspan="7" class="text-center text-muted py-5">
                    <i class="fe fe-shield" style="font-size: 2rem; display: block; margin-bottom: 8px;"></i>
                    No TPAs registered yet
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
   </section>
</template>

<script>
  import Navbar from '@/views/Navbar.vue'
  // Import component
     import Loading from 'vue-loading-overlay';
     // Import stylesheet
     import 'vue-loading-overlay/dist/vue-loading.css';
     // Init plugin


export default {
  components: {
     Navbar, Loading
  },
  data(){
    return{
      user:null,
      providers:"",
      agencies:"",
      category:"",
      edit:false,
      show:false,
      isLoading: false,
      fullPage: true,
      tpas:"",
      register:{
                email:"",
                phone_number:"",
                state:"",
                agency_name:"",
                ward:"",
                services_offered:"",
                localgovt:"",
                address1:"",
                tpa_id:"",
                address:"",
                firstname:"",
                lastname:"",
                organization_name:"",
                role:0,
                password:"",
                password_confirmation:""
            }
    }
  },
  computed: {
    // Agency admins (the agency account, or staff with user_role 1) edit HMOs.
    canManage() {
      return !!this.user && (this.user.type === "shis" || Number(this.user.user_role) === 1);
    },
  },
  beforeMount(){
    this.user = JSON.parse(localStorage.getItem('user'))
    this.axios.get(`/api/v1/auth/org_agency/407465`)
                .then(response => {
                    this.tpas = response.data.data
                    console.log(response)
                })
                .catch(error => {
                    console.error(error);
                })
  },
  methods:{

    registerUser(){
      this.user = JSON.parse(localStorage.getItem('user'))
        // the HMO code must be unique in the agency; check before the HMO's sign-in is created
        const code = (this.register.tpa_id || "").toLowerCase();
        if (code && (this.tpas || []).some((t) => String(t.tpa_id || "").trim().toLowerCase() === code)) {
          this.$toasted.error("Another HMO in this agency already has this HMO code.", { position: "top-center", duration: 5000 });
          return;
        }
        this.isLoading = true;
        this.axios.post('/api/v1/auth/registerProvider',{

          email : this.register.email,
          phone_number : this.register.phone_number,
          agency_name : this.register.agency_name,
          firstname : this.register.firstname,
          lastname : this.register.lastname,
          state : '2679',
          localgovt : '631',
          type : 'tpa',
          password : this.register.password,
          password_confirmation : this.register.password_confirmation,
          enrolled_by : 0,
        })
        .then(response=>{
          let tpa_user_id = response.data.data.id

            // add Organization
              this.show = false;
              console.log(response)
              this.axios.post('/api/v1/auth/orgenrollment',{
                user_id : tpa_user_id,
                email : this.register.email,
                phone_number : this.register.phone_number,
                organization_name : this.register.organization_name,
                // the agency this page lists (an admin employee's own id is not the agency)
                agency_id : 407465,
                category : this.category,
                tpa_id : this.register.tpa_id || null,
                address : this.register.address || null,
                  })

                  .then(response=>{
                      console.log(response);
                      this.getTPAs()
                      this.clearIt()
                      this.$toasted.info('Organization added Successfully!', {position: 'top-left', duration:5000 })

                  })
                  .catch(error=>{
                      console.log(error.response)
                      const data = (error.response && error.response.data) || {};
                      const first = data.errors && Object.values(data.errors)[0];
                      this.$toasted.error((first && first[0]) || 'The sign-in was created but the organisation was not saved', {position: 'top-center', duration:6000 })
                  })
                  // end of provider

            console.log(response);
            this.isLoading = false;
            this.getTPAs()

        })
        .catch(error=>{
            console.log(error.response)
            this.isLoading = false;
            this.$toasted.error('Sign up not Successful', {position: 'top-left', duration:5000 })

        })
    },
    editTPA(tpa){
      this.show = true;
      this.register.organization_name = tpa.organization_name;
      this.register.phone_number = tpa.phone_number;
      this.register.email = tpa.email;
      this.register.user_id = tpa.user_id;

    },
    getTPAs(){
      this.user = JSON.parse(localStorage.getItem('user'))

      this.axios.get(`/api/v1/auth/org_agency/407465`)
                  .then(response => {
                      this.tpas = response.data.data
                      console.log(response)
                  })
                  .catch(error => {
                      console.error(error);
                  })
    },

    clearIt(){
      this.register.firstname = "";
      this.register.lastname = "";
      this.register.email = "";
      this.register.phone_number = "";
      this.register.organization_name = "";
      this.register.tpa_id = "";
      this.register.address = "";
    },

  },
  created(){
    this.getTPAs()
  }

}
</script>
